import { computed, Ref } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { PageName, SummaryInfo } from '@/api/tracking';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { FeedSummaryFeature, FeedSummaryResponse } from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import { formatMarkdownForCopy } from '@/utils/survey/common';

export const useFeedSummaryActions = (
  props: {
    feedSummary: FeedSummaryResponse;
    pageName: PageName;
    themeId?: number;
  },
  summaryFeature: Ref<FeedSummaryFeature>,
  summaryInfo: Ref<SummaryInfo | undefined>,
) => {
  const router = useRouter();
  const { createSnackbar } = useSnackbar();
  const { getCurrentPathForTracking } = useCurrentPathForTracking();
  const layoutVariant = useLayoutVariant();

  const hasConvertedSurvey = computed(
    () =>
      props.feedSummary.generation_status === 'processed' &&
      props.feedSummary.has_converted_survey,
  );

  const convertToSummary = async () => {
    if (props.feedSummary.generation_status !== 'processed') return;
    const uuid = props.feedSummary.uuid;

    // テーマ要約
    if (props.themeId !== undefined) {
      let convertedUuid = props.feedSummary.converted_uuid;
      if (!convertedUuid) {
        convertedUuid = await api.convertThemeSummaryToSurvey(
          props.themeId,
          uuid,
        );
      }
      router.push(`/surveys/${convertedUuid}`);
      // パーソナルニュース要約
    } else {
      if (!hasConvertedSurvey.value) {
        await api.convertToSummary(uuid);
      }
      router.push(`/surveys/${uuid}`);
    }
  };

  const copySummary = async () => {
    if (props.feedSummary.generation_status !== 'processed') return;

    let summaryTextForCopy = formatMarkdownForCopy(
      props.feedSummary.model_response,
      props.feedSummary.sources,
    );

    const urls = props.feedSummary.sources.map(source => source.url);
    if (urls.length > 0) {
      const footnote = urls
        .map((url, index) => `[${index + 1}] ${url}`)
        .join('\n');
      summaryTextForCopy += `\n\nソース:\n${footnote}`;
    }

    try {
      await navigator.clipboard.writeText(summaryTextForCopy);

      createSnackbar({
        message: `コピーしました`,
        type: 'success',
      });
    } catch {
      createSnackbar({
        message: `コピーできませんでした`,
        type: 'error',
      });
    } finally {
      api.trackEvent(
        'summary_copied',
        {
          feature: summaryFeature.value,
          pageName: props.pageName,
          pageUrl: getCurrentPathForTracking(),
          layoutVariant,
        },
        undefined,
        undefined,
        summaryInfo.value,
      );
    }
  };

  return {
    hasConvertedSurvey,
    convertToSummary,
    copySummary,
  };
};
